import cn from 'classnames'
import { useEffect, useState, useRef } from 'react'
import { LazyLoadImage } from 'react-lazy-load-image-component'
import 'react-lazy-load-image-component/src/effects/blur.css'
import dynamic from 'next/dynamic'
import { Swiper, SwiperSlide } from 'swiper/react'
// Components
import Skeleton from 'react-loading-skeleton'
import PlayButton from '../videoPlayer/PlayButton'
import styles from './PetImage.module.scss'

const VideoModal = dynamic(() =>
  import('../videoPlayer/VideoModal').then((comp) => comp.VideoModal),
)

const Player = dynamic(() =>
  import('../videoPlayer/Player').then((comp) => comp.Player),
)

export default function PetImage({ dog, isMobile, loading }) {
  /* Swiper Handlers   */
  const ref = useRef(null)
  const showButtons = dog.images?.length > 2

  const [isBeginning, setIsBeginning] = useState(true)
  const [isEnd, setIsEnd] = useState(false)

  const goNext = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slideNext()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  const goPrev = async () => {
    if (ref.current !== null && ref.current.swiper !== null) {
      await ref.current.swiper.slidePrev()
      setIsBeginning(ref.current?.swiper?.isBeginning)
      setIsEnd(ref.current?.swiper?.isEnd)
    }
  }

  // Fulfill images array
  const [media, setMedia] = useState([])
  const [showVideoModal, setShowVideoModal] = useState(false)

  useEffect(() => {
    const firstImage = dog?.profilePicture || dog?.images?.[0]
    const rest = (dog.images || []).filter(
      (i) => i.medium !== firstImage.medium,
    )
    let dogImages = []

    if (!isMobile) {
      switch (dog?.images?.length) {
        case 1:
          dogImages = [firstImage, firstImage]
          break
        case 2:
          dogImages = [rest, dog?.images[Math.round(Math.random())]]
          break
        default:
          dogImages = []
      }
    }

    const dogMedia = [
      { ...(firstImage || {}), type: 'image' },
      ...(dog?.videos ?? []).map((v) => ({ ...v, type: 'video' })),
      ...(rest.length > 0 ? rest : dogImages).map((i) => ({
        ...i,
        id: dog.id + Math.random(),
        type: 'image',
      })),
    ]
    setMedia(dogMedia)
  }, [dog, loading, isMobile])

  const totalNumOfData = media.length
  const initialVideo = dog.videos?.[0]
  if (loading) {
    return (
      <div className={cn(styles.images, styles.skeleton)}>
        <Skeleton width="100%" height="100%" />
      </div>
    )
  } else {
    return (
      <div
        className={cn({
          [styles.images]: true,
          [styles.hasBanner]: !dog?.isAvailable,
        })}
      >
        {showButtons && (
          <>
            <button
              className={cn({
                [styles.swiperButton]: true,
                [styles.swiperPrev]: !isBeginning,
                [styles.hide]: isBeginning,
              })}
              onClick={goPrev}
            />
            <button
              className={cn({
                [styles.swiperButton]: true,
                [styles.swiperNext]: !isEnd,
                [styles.hide]: isEnd,
              })}
              onClick={goNext}
            />
          </>
        )}

        <Swiper
          speed={1000}
          slidesPerView="auto"
          spaceBetween={10}
          grabCursor
          breakpoints={{
            360: {
              slidesPerView: 1,
              spaceBetween: 2,
            },
            540: {
              slidesPerView: 'auto',
              spaceBetween: 10,
            },
          }}
          ref={ref}
        >
          {media.map((c, i) => {
            return c.type === 'video' ? (
              <SwiperSlide key={i}>
                <div className={styles.video}>
                  <Player video={c} />
                  {totalNumOfData > 1 && (
                    <>
                      <div className={styles.counter}>
                        {i + 1 + '/' + totalNumOfData}
                      </div>

                      <div className={styles.pagination}>
                        {media.map((_page, pageI) => (
                          <div
                            key={c.id + _page.id}
                            className={cn({
                              [styles.page]: true,
                              [styles.active]: i === pageI,
                            })}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </SwiperSlide>
            ) : (
              <SwiperSlide key={i}>
                <div className={styles.image}>
                  <LazyLoadImage
                    alt={`Puppy id ${c.id}`}
                    src={process.env.NEXT_PUBLIC_MEDIA_BUCKET + c?.medium}
                    width={420}
                    height={420}
                    effect="blur"
                  />

                  {i === 0 &&
                    !!initialVideo &&
                    initialVideo?.high &&
                    isMobile && (
                      <PlayButton
                        height={50}
                        width={50}
                        containerStyle={{
                          position: 'absolute',
                          top: 10,
                          right: 10,
                        }}
                        onClick={() => setShowVideoModal(true)}
                      />
                    )}
                  {totalNumOfData > 1 && (
                    <>
                      <div className={styles.counter}>
                        {i + 1 + '/' + totalNumOfData}
                      </div>

                      <div className={styles.pagination}>
                        {media.map((_page, pageI) => (
                          <div
                            key={c.id + _page.id}
                            className={cn({
                              [styles.page]: true,
                              [styles.active]: i === pageI,
                              [styles.prev]:
                                i + 1 === 1 || i - 1 === media.length - 2,
                            })}
                          />
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </SwiperSlide>
            )
          })}
        </Swiper>
        {!!initialVideo && (
          <VideoModal
            isOpen={showVideoModal}
            close={() => setShowVideoModal(false)}
            video={initialVideo}
          />
        )}
      </div>
    )
  }
}
